import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Lock, Zap } from "lucide-react";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/components/Analytics";

// Speculation Rules + prefetch helpers — warms up the Stripe Payment Link
// connection (and where supported, prerenders the page) so the post-submit
// redirect feels instant instead of a 1–3s blank wait.
function warmUpStripeLink(url) {
  if (!url || typeof document === "undefined") return;
  // 1. Standard prefetch hint — most browsers will at least open the TCP/TLS
  //    socket and start the HTML fetch into the HTTP cache.
  if (!document.querySelector(`link[rel="prefetch"][data-stripe-warm="1"]`)) {
    const l = document.createElement("link");
    l.rel = "prefetch";
    l.as = "document";
    l.href = url;
    l.crossOrigin = "anonymous";
    l.setAttribute("data-stripe-warm", "1");
    document.head.appendChild(l);
  }
  // 2. Speculation Rules (Chromium 121+) — actually prerenders the document
  //    so when we navigate, it's already in memory. Falls back silently on
  //    other browsers.
  if (HTMLScriptElement.supports && HTMLScriptElement.supports("speculationrules")) {
    if (!document.querySelector(`script[type="speculationrules"][data-stripe-warm="1"]`)) {
      const s = document.createElement("script");
      s.type = "speculationrules";
      s.setAttribute("data-stripe-warm", "1");
      s.textContent = JSON.stringify({
        prerender: [{ source: "list", urls: [url], eagerness: "moderate" }],
      });
      document.head.appendChild(s);
    }
  }
}

export default function Membership() {
  const [params] = useSearchParams();
  const sourceQR = params.get("source") === "quote-review";
  const [form, setForm] = useState({
    full_name: params.get("name") || "",
    email: params.get("email") || "",
    phone: "",
    park_name: "", plot_number: "", town_location: "",
    park_home_type: "",
    referral_source: sourceQR ? "Quote Review" : "",
    agreed_terms: false, waived_cooling_off: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [pendingMsg, setPendingMsg] = useState("");

  // Pull the live Stripe Membership Payment Link from public config and
  // warm it up immediately — by the time the user finishes the form, the
  // browser has already done DNS/TLS/HTML for the destination.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/config");
        if (cancelled) return;
        if (data?.membership_payment_link) {
          warmUpStripeLink(data.membership_payment_link);
        }
      } catch { /* preconnect hints in index.html still apply */ }
    })();
    return () => { cancelled = true; };
  }, []);

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.agreed_terms || !form.waived_cooling_off) {
      toast.error("Please agree to both legal checkboxes to continue");
      return;
    }
    setSubmitting(true);
    setPendingMsg("");
    try {
      const { data } = await api.post("/members/signup", form);
      trackEvent("begin_checkout", {
        value: 99,
        currency: "GBP",
        items: [{ item_name: "PHS Membership Year 1", price: 99 }],
        source: form.referral_source || "direct",
      });
      if (data.checkout_url) {
        // Show an immediate "redirecting" state — keeps the user from clicking
        // submit again while the browser hands off to Stripe.
        setRedirecting(true);
        // Hint the browser one more time in case /api/config didn't return a
        // membership link (e.g. coupon-only flows that build a different URL).
        warmUpStripeLink(data.checkout_url);
        window.location.replace(data.checkout_url);
        return;
      }
      setPendingMsg(data.message || "Member record created. We'll be in touch.");
      toast.success("Application received");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="membership-page">
      <SEO
        title="Join Park Home Support — £99 First Year | Annual Health Check Included"
        description="An annual support membership for UK park home owners. Includes an external Park Home Health Check each year, quote reviews, technical support, insurance guidance and maintenance responsibility advice. £99 first year, then £249/year."
        canonical="https://parkhomesupport.co.uk/membership"
      />
      <section className="bg-[#F7F4ED] section-pad">
        <div className="container-phs">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <span className="eyebrow">Join the Plan</span>
              <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-tight text-[#071A33]">
                One membership. Year-round support and a yearly Health Check.
              </h1>
              <p className="mt-6 text-[#334155] text-lg">
                Create your member record and complete a secure Stripe payment of <strong>£99</strong> for your first year. Renews at £249/year unless cancelled. Includes your <strong>Annual External Park Home Health Check</strong> as a member benefit.
              </p>

              <div className="mt-8 card-dark">
                <span className="text-[#B88A2A] tracking-[0.22em] uppercase text-xs">First Year</span>
                <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                  <span className="price-display text-5xl">
                    <span className="price-currency">£</span>
                    <span className="price-value">99</span>
                  </span>
                  <span className="text-white/70">then £249/yr</span>
                </div>
                <ul className="mt-5 space-y-2 text-sm text-white/85">
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#B88A2A]"/> <span><strong>Annual External Park Home Health Check</strong> included</span></li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#B88A2A]"/> Independent Quote Reviews</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#B88A2A]"/> Technical Support</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#B88A2A]"/> Insurance Guidance</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#B88A2A]"/> Maintenance Responsibility Advice</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#B88A2A]"/> Cancel anytime — runs to period end</li>
                </ul>
                <p className="mt-4 text-[11px] text-white/55 leading-relaxed">
                  The Annual External Park Home Health Check is an external visual condition review of your home with maintenance guidance. It is not a regulated survey, structural survey or warranty.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              {sourceQR && (
                <div className="bg-[#064E2E] text-white px-5 py-4 mb-5 flex items-start gap-3 border-l-4 border-[#B88A2A]" data-testid="qr-priority-banner">
                  <Zap size={20} className="text-[#B88A2A] mt-0.5 shrink-0"/>
                  <div className="text-sm">
                    <p className="font-medium">Your quote review will be prioritised the moment you join.</p>
                    <p className="text-white/80 mt-0.5">We&apos;ll move your case to the priority queue and follow up with the full breakdown by email.</p>
                  </div>
                </div>
              )}
              <form className="card-default" onSubmit={onSubmit} data-testid="membership-form">
                <h2 className="font-display text-2xl text-[#071A33]">Your details</h2>

                <div className="grid sm:grid-cols-2 gap-5 mt-6">
                  <Field label="Full name" required>
                    <input className="field-input" required value={form.full_name} onChange={(e)=>update("full_name", e.target.value)} data-testid="member-full-name"/>
                  </Field>
                  <Field label="Email" required>
                    <input className="field-input" type="email" required value={form.email} onChange={(e)=>update("email", e.target.value)} data-testid="member-email"/>
                  </Field>
                  <Field label="Phone" required>
                    <input className="field-input" required value={form.phone} onChange={(e)=>update("phone", e.target.value)} data-testid="member-phone"/>
                  </Field>
                  <Field label="Town / Location" required>
                    <input className="field-input" required value={form.town_location} onChange={(e)=>update("town_location", e.target.value)} data-testid="member-town"/>
                  </Field>
                  <Field label="Park name" required>
                    <input className="field-input" required value={form.park_name} onChange={(e)=>update("park_name", e.target.value)} data-testid="member-park-name"/>
                  </Field>
                  <Field label="Plot number" required>
                    <input className="field-input" required value={form.plot_number} onChange={(e)=>update("plot_number", e.target.value)} data-testid="member-plot-number"/>
                  </Field>
                  <Field label="Park home type">
                    <select className="field-input" value={form.park_home_type} onChange={(e)=>update("park_home_type", e.target.value)} data-testid="member-park-home-type">
                      <option value="">Select…</option>
                      <option>BS3632 residential</option>
                      <option>Pre-1989 model</option>
                      <option>Twin-unit</option>
                      <option>Single-unit</option>
                      <option>Not sure</option>
                    </select>
                  </Field>
                  <Field label="How did you hear about us?">
                    <input className="field-input" value={form.referral_source} onChange={(e)=>update("referral_source", e.target.value)} data-testid="member-referral"/>
                  </Field>
                </div>

                <div className="mt-7 space-y-3 text-sm text-[#334155]">
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={form.agreed_terms} onChange={(e)=>update("agreed_terms", e.target.checked)} data-testid="member-agree-terms"/>
                    <span>I agree to the <Link to="/terms" className="link-underline">Terms & Conditions</Link> and <Link to="/privacy" className="link-underline">Privacy Policy</Link>.</span>
                  </label>
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={form.waived_cooling_off} onChange={(e)=>update("waived_cooling_off", e.target.checked)} data-testid="member-waive-cooling-off"/>
                    <span>I request immediate access to the service and understand that once I request support or receive advice, my right to cancel is waived and the membership becomes non-refundable.</span>
                  </label>
                </div>

                <button type="submit" disabled={submitting || redirecting} className="btn-primary mt-7 w-full sm:w-auto" data-testid="member-submit-btn">
                  {redirecting ? (
                    <><Loader2 className="animate-spin" size={16}/> Redirecting to Stripe…</>
                  ) : submitting ? (
                    <><Loader2 className="animate-spin" size={16}/> Processing…</>
                  ) : (
                    <>Join Now – £99 First Year <ArrowRight size={16}/></>
                  )}
                </button>

                <p className="mt-4 text-xs text-[#475569] flex items-center gap-1.5">
                  <Lock size={12}/> Secure payment via Stripe. Renews at £249/year. No refunds once service has been used.
                </p>

                {pendingMsg && (
                  <div className="mt-5 bg-[#F7F4ED] border border-[#E2DCD0] p-4 text-sm text-[#071A33]" data-testid="pending-stripe-msg">
                    {pendingMsg}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="field-label">{label}{required ? <span className="text-[#B88A2A]"> *</span> : null}</label>
      {children}
    </div>
  );
}
